/**
 * TypeformTracker - GA4 / GTM analytics for the embedded Typeform.
 *
 * The form is a cross-origin iframe, so we read its postMessage events through one
 * origin-checked listener instead of its DOM.
 * Technique:
 * https://www.analyticsmania.com/post/track-paperform-form-submissions-with-google-analytics-4/
 * https://www.simoahava.com/analytics/cookieless-tracking-cross-site-iframes/
 *
 *
 * dataLayer events included:
 *  - typeform.start            tracks funnel entry point
 *  - typeform.step_change      tracks each new question screen reached, once each
 *  - typeform.outcome          tracks submitted, or an ending button clicked
 *  - typeform.outcome_redirect tracks destination link, plus outcome_program
 *
 *  Redirects are detected three ways, none of which intercept the navigation:
 *  1. Typeform's own redirect messages. Clears 3's marker so both can't count.
 *  2. Navigation API, armed only after an outcome.
 *  3. Otherwise a sessionStorage marker, read back by the next page to load.
 * */

const TYPEFORM_ORIGINS = ['https://form.typeform.com', 'https://form.typeform.eu'];
const SCREEN_MESSAGE_TYPE = 'form-screen-changed';
const START_MESSAGE_TYPE = 'form-started';
const OUTCOME_MESSAGE_TYPES = ['form-submit', 'thank-you-screen-button-click'];
const REDIRECT_MESSAGE_TYPES = ['redirect-after-submit', 'thank-you-screen-redirect'];
const LANDING_MARKER_KEY = 'typeformOutcomeMarker';
const LANDING_MARKER_MAX_AGE_MS = 60000; // ignore a stale marker from an abandoned tab

const outcomeSentByForm = new Set();
const startSentByForm = new Set();

// Only form-started carries responseId, and the ending click can beat form-submit to it.
const responseIdByForm = new Map();

// Distinct refs per form. Doubles as the seen-set that suppresses back-navigation, so
// step_number is depth reached, not position. form-screen-changed carries no ordinal or
// total, hence no total_steps.
const questionsSeenByForm = new Map();

// Feature-detected, not UA-matched - Chrome's UA also contains "Safari".
const supportsNavigationAPI = Boolean(window.navigation);

let outcomeReached = false;
let redirectSent = false;
let outcomeFormId = null;

// Absolute URLs only. Resolving against the current page instead would turn a malformed
// destination into this page's own slug, which reads as a real program name in GA4.
// All three redirect routes supply an absolute URL, so nothing legitimate is lost.
const programFromUrl = url => {
    try {
        return new URL(url).pathname.split('/').filter(Boolean).pop() || null;
    } catch (e) {
        return null;
    }
};

const pushToDataLayer = data => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...data, event_timestamp: new Date().toISOString() });
};

const sendRedirect = (url, target, eventType, formId) => {
    if (!url || redirectSent) return;
    redirectSent = true;

    try {
        sessionStorage.removeItem(LANDING_MARKER_KEY); // stop 3 re-reporting this
    } catch (e) {
        // best-effort
    }

    const id = formId || outcomeFormId;

    pushToDataLayer({
        event: 'typeform.outcome_redirect',
        event_type: eventType,
        form_id: id,
        response_id: responseIdByForm.get(id),
        outcome_program: programFromUrl(url),
        redirect_url: url,
        redirect_target: target || '_parent'
    });
};

const recordScreen = (formId, ref, eventType) => {
    if (!ref) return;

    if (!questionsSeenByForm.has(formId)) questionsSeenByForm.set(formId, new Set());
    const questionsSeen = questionsSeenByForm.get(formId);

    // Only newly reached screens count. Typeform re-fires form-screen-changed when the
    // respondent goes back, and again on the way forward through screens they've already
    // seen, so without this a single question reports as several steps.
    if (questionsSeen.has(ref)) return;
    questionsSeen.add(ref);

    pushToDataLayer({
        event: 'typeform.step_change',
        event_type: eventType,
        form_id: formId,
        question_ref: ref,
        step_number: questionsSeen.size // distinct screens seen, so the deepest step reached
    });
};

const sendStart = (formId, responseId, eventType) => {
    if (startSentByForm.has(formId)) return; // a respondent starts a form once
    startSentByForm.add(formId);
    if (responseId) responseIdByForm.set(formId, responseId);

    pushToDataLayer({
        event: 'typeform.start',
        event_type: eventType,
        form_id: formId,
        response_id: responseId // joins this event to typeform.outcome
    });
};

const sendOutcome = (formId, responseId, eventType) => {
    if (outcomeSentByForm.has(formId)) return; // submit and ending click both count, once
    outcomeSentByForm.add(formId);
    outcomeReached = true;
    outcomeFormId = formId;

    pushToDataLayer({
        event: 'typeform.outcome',
        event_type: eventType,
        form_id: formId,
        // The ending click carries no responseId, so fall back to form-started's.
        response_id: responseId || responseIdByForm.get(formId)
    });

    if (supportsNavigationAPI) return; // 2 will catch the redirect, no marker needed

    try {
        sessionStorage.setItem(LANDING_MARKER_KEY, String(Date.now()));
    } catch (e) {
        // best-effort; sessionStorage can throw in locked-down contexts
    }
};

window.addEventListener('message', event => {
    if (!TYPEFORM_ORIGINS.includes(event.origin)) return;

    const { type, formId, responseId, ref, url, target } = event.data || {};
    if (!type) return;

    if (type === SCREEN_MESSAGE_TYPE) {
        recordScreen(formId, ref, type);
    } else if (type === START_MESSAGE_TYPE) {
        sendStart(formId, responseId, type);
    } else if (OUTCOME_MESSAGE_TYPES.includes(type)) {
        sendOutcome(formId, responseId, type);
    } else if (REDIRECT_MESSAGE_TYPES.includes(type)) {
        sendRedirect(url, target, type, formId);
    }
});

// 2. Gated on outcomeReached so ordinary site navigation isn't counted.
if (supportsNavigationAPI) {
    window.navigation.addEventListener('navigate', event => {
        if (!outcomeReached || event.hashChange || event.navigationType === 'reload') return;
        sendRedirect(event.destination.url, null, 'navigation-api');
    });
}

// 3. Runs on every page, so the landing page reads the marker back.
if (!supportsNavigationAPI) {
    let markerTime;
    try {
        markerTime = Number(sessionStorage.getItem(LANDING_MARKER_KEY));
        sessionStorage.removeItem(LANDING_MARKER_KEY); // one-shot, acted on or not
    } catch (e) {
        markerTime = null;
    }
    if (markerTime && Date.now() - markerTime <= LANDING_MARKER_MAX_AGE_MS) {
        sendRedirect(window.location.href, null, 'session-marker');
    }
}
